/**
 * THE CABINET'S SESSION, AS A PURE REDUCER.
 *
 * WHY A REDUCER RATHER THAN SIX `useState` CALLS. There is exactly one rule in this feature that
 * must hold for every combination of events, and it is not a rendering rule:
 *
 *     A 503 MUST NEVER END A SESSION.
 *
 * adr/0010-firebase-integration.md rules that the guard answers "503 for `cannot verify right
 * now` and 401 only for `this token is invalid`, so the apps retry instead of signing people out",
 * and the 503 response component says why in its own words: "so that a Google outage does not sign
 * every author out of the cabinet". With ~530 authors and one operator, the failure mode is that a
 * Google incident becomes a support incident. A rule of that shape belongs somewhere it can be
 * asserted over the whole state space, not inside a component.
 *
 * SO: no React here, no promises, no side effects. `identity` is plain data — never the live SDK
 * session object — precisely so this file stays testable and so nothing can smuggle an
 * `getIdToken()` call into a state transition.
 */
import type { ApiFailure } from '../api/failure';
import { actionFor } from '../api/failure';
import type { AuthorSession } from '../api/authors-api';
import type { AuthIdentity } from '../firebase/auth-port';

export type SessionPhase =
  /** Waiting for the SDK's first answer about this origin. Nothing may be rendered as "signed out" yet. */
  | 'booting'
  /** Nobody is signed in. The cold-start door. */
  | 'signed-out'
  /** A popup is open, or a hand-off code is being exchanged. */
  | 'signing-in'
  /** Signed in; `GET /v1/me` is in flight. */
  | 'loading-session'
  /** Signed in and the session is loaded. The cabinet renders. */
  | 'ready'
  /**
   * The platform could not answer right now — 503, 429, or no network at all. THE SESSION IS
   * HELD. This phase exists so that "we cannot check right now" has somewhere to live that is not
   * "signed out".
   */
  | 'reconnecting'
  /** An answer we understood and cannot act on. Rendered, never retried in a loop. */
  | 'refused';

/** Why a cold-start sign-in did not happen. None of these is an HTTP status; none reaches our API. */
export type SignInFailure =
  /** THE BROWSER REFUSED THE WINDOW. Named failure state one. */
  | 'popup-blocked'
  /** Apple or Google could not be reached. */
  | 'provider-unreachable'
  /** Anything else the adapter could not classify. */
  | 'unknown';

export interface SessionState {
  readonly phase: SessionPhase;
  /** Who is signed in, as plain data. Survives every retryable failure. */
  readonly identity: AuthIdentity | null;
  /** The last good `GET /v1/me`. Kept through `reconnecting` so the cabinet keeps rendering. */
  readonly session: AuthorSession | null;
  /** Set only while `phase === 'signed-out'` after a failed cold start. */
  readonly signInFailure: SignInFailure | null;
  /** Set while `phase` is `reconnecting` or `refused`. */
  readonly failure: ApiFailure | null;
  /** How long the platform asked us to wait, in seconds. Drives the countdown, never a clock read. */
  readonly retryAfterSeconds: number | null;
  /**
   * True once a bare 401 has already been answered with one forced token refresh. The second bare
   * 401 signs out: retrying a credential the platform will not accept is a loop.
   */
  readonly tokenRefreshAttempted: boolean;
  /**
   * True once the terms interstitial has been on screen for this person in this page's lifetime.
   * The gate puts it in front of a cabinet screen AT MOST ONCE, because the corpus wants "an
   * interstitial, not a wall" (docs/design/research/26-authors-identity-application-research.md)
   * - see `termsInterstitialDue` below. A reload asks again; a different person starts at false.
   */
  readonly termsInterstitialShown: boolean;
}

export const initialSessionState: SessionState = {
  phase: 'booting',
  identity: null,
  session: null,
  signInFailure: null,
  failure: null,
  retryAfterSeconds: null,
  tokenRefreshAttempted: false,
  termsInterstitialShown: false,
};

/** The one member of `ApiFailure` that can be a refused credential. */
export type UnauthenticatedFailure = Extract<ApiFailure, { readonly kind: 'unauthenticated' }>;

export type SessionEvent =
  /** The SDK reported what it restored for this origin — an identity, or null. Fires on every change. */
  | { readonly type: 'identity-changed'; readonly identity: AuthIdentity | null }
  /** A popup was opened, or a hand-off exchange started. */
  | { readonly type: 'sign-in-started' }
  /** The person closed the popup. NOT an error: back to idle, silently. */
  | { readonly type: 'sign-in-cancelled' }
  | { readonly type: 'sign-in-failed'; readonly reason: SignInFailure }
  /** `GET /v1/me` was sent. */
  | { readonly type: 'session-requested' }
  | { readonly type: 'session-loaded'; readonly session: AuthorSession }
  | { readonly type: 'session-failed'; readonly failure: ApiFailure }
  /** A forced token refresh was spent on a bare 401. */
  | { readonly type: 'token-refresh-attempted' }
  /**
   * A SCREEN'S OWN REQUEST - not `GET /v1/me` - was refused for its credential after the one forced
   * refresh the rule allows, or with the challenge that forbids one. Reported by
   * ./send-with-session.ts through the provider, which has already checked it is about this person.
   */
  | { readonly type: 'credential-refused'; readonly failure: UnauthenticatedFailure }
  /** The terms interstitial is on screen. See `termsInterstitialDue`. */
  | { readonly type: 'terms-interstitial-shown' }
  /** Local sign-out. There is no API call behind this — the cabinet holds no server session. */
  | { readonly type: 'signed-out' };

export function sessionReducer(state: SessionState, event: SessionEvent): SessionState {
  switch (event.type) {
    case 'identity-changed': {
      if (event.identity === null) {
        return {
          ...initialSessionState,
          phase: 'signed-out',
          // A sign-in failure survives the SDK telling us there is still nobody signed in —
          // otherwise a blocked popup would clear its own explanation off the screen.
          signInFailure: state.signInFailure,
        };
      }
      const sameAccount = state.identity?.uid === event.identity.uid;
      return {
        ...state,
        phase: 'loading-session',
        identity: event.identity,
        // A different uid means a different person: the previous session is not theirs to see.
        session: sameAccount ? state.session : null,
        signInFailure: null,
        failure: null,
        retryAfterSeconds: null,
        tokenRefreshAttempted: false,
        // What this person has already been shown stays shown; a different person has seen nothing.
        termsInterstitialShown: sameAccount ? state.termsInterstitialShown : false,
      };
    }

    case 'sign-in-started':
      return { ...state, phase: 'signing-in', signInFailure: null, failure: null };

    case 'sign-in-cancelled':
      // Someone who changed their mind has not failed at anything.
      return { ...state, phase: 'signed-out', signInFailure: null };

    case 'sign-in-failed':
      return { ...state, phase: 'signed-out', signInFailure: event.reason };

    case 'session-requested':
      return {
        ...state,
        // Keep rendering the last good session while re-reading. A screen that blanks on every
        // refresh is a screen that flickers on every retry during an outage.
        phase: state.session === null ? 'loading-session' : state.phase,
        failure: null,
        retryAfterSeconds: null,
      };

    case 'session-loaded':
      return {
        ...state,
        phase: 'ready',
        session: event.session,
        failure: null,
        retryAfterSeconds: null,
        tokenRefreshAttempted: false,
      };

    case 'session-failed':
      return applyFailure(state, event.failure);

    case 'token-refresh-attempted':
      return { ...state, tokenRefreshAttempted: true };

    case 'credential-refused':
      // The client that saw it has ALREADY spent the one forced refresh, or was told by the
      // challenge not to - so the refresh is counted as spent and the failure goes through the one
      // place below that may end a session, rather than beside it. A bare 401 here therefore signs
      // out exactly as the second bare 401 on `GET /v1/me` does.
      return applyFailure({ ...state, tokenRefreshAttempted: true }, event.failure);

    case 'terms-interstitial-shown':
      return state.termsInterstitialShown ? state : { ...state, termsInterstitialShown: true };

    case 'signed-out':
      return { ...initialSessionState, phase: 'signed-out' };
  }
}

/**
 * THE ONLY PLACE A FAILURE CAN END A SESSION, so there is only one place to get it wrong.
 *
 *   sign-out              only ever from a 401 whose `WWW-Authenticate` carried
 *                         `error="invalid_token"` — the challenge the bearer surfaces send "only
 *                         when a token was presented and failed ... the one that stops a client
 *                         retrying a token that will never work" (shared.yaml) — or from a
 *                         second bare 401 after the one forced refresh has been spent.
 *   refresh-token-once    a bare 401. Possibly our own staleness; worth exactly one refresh.
 *   hold-session-and-retry  EVERY 503, every 429, every network failure, every undeclared status.
 *                         The identity and the last good session are carried through untouched.
 *   show-refusal          400 / 403 / 409 / 422. The platform understood us and said no; nothing
 *                         about the session changed.
 */
function applyFailure(state: SessionState, failure: ApiFailure): SessionState {
  switch (actionFor(failure)) {
    case 'sign-out':
      return { ...initialSessionState, phase: 'signed-out' };

    case 'refresh-token-once':
      if (state.tokenRefreshAttempted) {
        // The refresh has already been spent on this session. A third attempt is a loop.
        return { ...initialSessionState, phase: 'signed-out' };
      }
      return { ...state, phase: 'loading-session', failure, retryAfterSeconds: null };

    case 'hold-session-and-retry':
      return {
        ...state,
        phase: 'reconnecting',
        // IDENTITY AND SESSION ARE CARRIED THROUGH DELIBERATELY. This line is the rule.
        identity: state.identity,
        session: state.session,
        failure,
        retryAfterSeconds: retryAfterOf(failure),
      };

    case 'show-refusal':
      return { ...state, phase: 'refused', failure, retryAfterSeconds: null };
  }
}

function retryAfterOf(failure: ApiFailure): number | null {
  switch (failure.kind) {
    case 'identity-unavailable':
    case 'rate-limited':
    case 'unexpected':
      return failure.retryAfterSeconds;
    default:
      return null;
  }
}

/**
 * G30, AS A QUESTION ABOUT THE SIGNED-IN PERSON RATHER THAN ABOUT AN API ANSWER.
 *
 * "An anonymous Firebase session must not reach the application flow." The check is a property of
 * the token and the remedy is a LINK that keeps the same uid — never a refusal, never a second
 * account (docs/design/research/26-authors-identity-application-research.md;
 * adr/0010-firebase-integration.md).
 *
 * There is no API answer to branch on: the 65-code `ErrorCode` registry has no code meaning "you
 * are signed in anonymously", and `createCabinetHandoffCode` accepts any verified token — so an
 * anonymous reader can mint a code, land in the cabinet and be told `canApply: true`. Until a code
 * exists, the cabinet asks for the link at the FRONT of the flow, which is where the corpus says it
 * belongs anyway.
 */
export function needsProviderLink(state: SessionState): boolean {
  return state.identity !== null && state.identity.isAnonymous;
}

/** True when the cabinet may render its shell: ANY author row (design 25), a DISABLED one's too. */
export function cabinetIsReachable(state: SessionState): boolean {
  return state.session?.cabinetReachable === true;
}

/**
 * THE TERMS INTERSTITIAL, AS A QUESTION ABOUT THE LOADED SESSION: should the gate put `/terms` in
 * front of the cabinet screen this person is opening?
 *
 * `termsAcceptanceRequired` is "True when an approved author owes a submission document" - the
 * author agreement or the editorial standards - and never blocks a payout; the server sends it
 * since design 25 (docs/design/07-api/authors-api.yaml). The cutover is ruled: the ~530
 * existing authors are "asked to accept, on one interstitial screen with one click"
 * (docs/design/04-schema.md; decision-log row 92). WHEN and HOW OFTEN it appears is
 * screen flow, which is ours (decision-log rows 99-100). The four conditions, each for a reason:
 *
 *   phase 'ready'         a fresh answer. A session held through an outage is not re-asked about.
 *   not yet shown         AT MOST ONCE per visit. What the stale acceptance blocks is the author
 *                         capability; reading the cabinet, editing a draft and payout details are NOT
 *                         blocked, and "the cabinet's job is to make the block visible and one click
 *                         from resolvable — an interstitial, not a wall" (research/26:465-475).
 *   canUseEditor          the capability the terms gate. An applicant accepts INSIDE the application
 *                         flow (`routes/apply/index.tsx`) and must never be pulled out of it;
 *                         a disabled author has no capability for an acceptance to unblock.
 *   === true              the field is optional on `AuthorSession`; absent is not a demand.
 */
export function termsInterstitialDue(state: SessionState): boolean {
  return (
    state.phase === 'ready' &&
    !state.termsInterstitialShown &&
    state.session?.canUseEditor === true &&
    state.session.termsAcceptanceRequired === true
  );
}

/**
 * ★ AN APPLICANT WRITING BEFORE APPROVAL (design 25, D6): the session lets them write drafts and
 * add their pictures (`canWriteDrafts`, at most 50 pictures until approval) but not send anything
 * for review (`canUseEditor`, approved only - the server answers `AUTHOR_APPROVAL_REQUIRED`). The
 * editor asks this to put "Your author application is approved first" where Submit would be.
 *
 * `=== true` on `canWriteDrafts`: a server from before design 25 sends none, and absent is not a
 * yes - the editor then keeps its Submit, and the server's own refusal says the rest.
 */
export function writesDraftsBeforeApproval(state: SessionState): boolean {
  return state.session?.canUseEditor !== true && state.session?.canWriteDrafts === true;
}
